// ConservationReportForm.jsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { ArrowLeft } from "lucide-react";
import { VISION_API_URL } from "@/config/domains";

const ConservationReportForm = () => {
  const { artworkId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);

  const [form, setForm] = useState({
    last_full_report: "",
    last_inspection: "",
    last_transport: "",
    risk_level: "Modéré",
    next_inspection_due: "",
    next_full_report_due: "",
    recommendation: "",
  });

  const [selectedZone, setSelectedZone] = useState("surface");
  const [uploadedFilenames, setUploadedFilenames] = useState([]); // filenames des nouveaux uploads
  const [previewUrls, setPreviewUrls] = useState([]);             // URLs locales pour aperçu immédiat
  const [existingImages, setExistingImages] = useState([]);       // URLs signées fraîches pour affichage
  const [existingFilenames, setExistingFilenames] = useState([]); // filenames permanents (sauvegardés en DB)
  const [zoneMapping, setZoneMapping] = useState([]);             // zones des nouveaux uploads
  const [existingZones, setExistingZones] = useState([]);         // zones des images existantes

  // Supprime un nouveau upload (pas encore sauvegardé)
  const removePreview = (index) => {
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
    setUploadedFilenames((prev) => prev.filter((_, i) => i !== index));
    setZoneMapping((prev) => prev.filter((_, i) => i !== index));
  };

  // Supprime une image existante (déjà en DB)
  const removeExistingImage = (index) => {
    if (window.confirm("Supprimer cette image ?")) {
      setExistingImages((prev) => prev.filter((_, i) => i !== index));
      setExistingZones((prev) => prev.filter((_, i) => i !== index));
      setExistingFilenames((prev) => prev.filter((_, i) => i !== index));
    }
  };

  // Chargement du rapport existant + génération d'URLs signées fraîches
  useEffect(() => {
    const fetchReport = async () => {
      const { data } = await supabase
        .from("conservation_reports")
        .select("*")
        .eq("artwork_id", artworkId)
        .order("created_at", { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        const r = data[0];
        setReport(r);
        setForm(r);

        const filenames = r.image_urls || [];

        // Extraire les zones depuis les filenames
        setExistingZones(
          filenames.map((f) => {
            const match =
              typeof f === "string"
                ? f.match(/conservation-[^-]+-([^-]+(?:-[^-]+)*?)-\d{13}-/)
                : null;
            return match ? match[1] : "zone inconnue";
          })
        );

        // Stocker les filenames permanents
        setExistingFilenames(filenames);

        // Générer des URLs signées fraîches (1h) pour l'affichage
        const signed = await Promise.all(
          filenames.map(async (filename) => {
            const { data: sd, error } = await supabase.storage
              .from("artworks-images")
              .createSignedUrl(filename, 60 * 60);
            if (error) console.error("Erreur URL signée :", filename, error);
            return sd?.signedUrl || null;
          })
        );
        setExistingImages(signed.filter(Boolean));
      }

      setLoading(false);
    };

    fetchReport();
  }, [artworkId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (files) => {
    const newFilenames = []; // filenames permanents à sauvegarder en DB
    const localPreviews = [];
    const zones = [];

    for (let file of files) {
      const zone = selectedZone || "zone-non-specifiee";
      const filename = `conservation-${artworkId}-${zone}-${Date.now()}-${file.name}`;

      const { error } = await supabase.storage
        .from("artworks-images")
        .upload(filename, file, {
          cacheControl: "3600",
          upsert: false,
        });

      if (!error) {
        newFilenames.push(filename); // ✅ on stocke le chemin, pas l'URL
        localPreviews.push(URL.createObjectURL(file)); // aperçu local immédiat
        zones.push(zone);
      } else {
        console.error("Erreur upload :", error);
      }
    }

    setPreviewUrls((prev) => [...prev, ...localPreviews]);
    setUploadedFilenames((prev) => [...prev, ...newFilenames]);
    setZoneMapping((prev) => [...prev, ...zones]);

    console.log("✅ Filenames uploadés :", newFilenames);

    if (newFilenames.length > 0) {
      setAnalyzing(true);

      // Générer des URLs signées temporaires uniquement pour l'analyse IA
      const signedUrls = await Promise.all(
        newFilenames.map(async (filename) => {
          const { data } = await supabase.storage
            .from("artworks-images")
            .createSignedUrl(filename, 60 * 60); // 1h, juste pour l'analyse
          return data?.signedUrl || null;
        })
      );
      const validUrls = signedUrls.filter(Boolean);

      const result = await triggerVisionAnalysis(validUrls);
      setAnalyzing(false);

      if (result.success) {
        setForm((prev) => ({
          ...prev,
          risk_level: result.risk_level,
          recommendation: result.recommendation,
        }));
      }
    }
  };

  const triggerVisionAnalysis = async (urls) => {
    try {
      const apiBase = VISION_API_URL;
      const response = await fetch(`${apiBase}/analyse-images`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ imageUrls: urls }),
      });
      const result = await response.json();
      console.log("Résultat IA:", result);
      return result;
    } catch (error) {
      console.error("Erreur d'analyse IA:", error);
      return { success: false };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      ...form,
      artwork_id: artworkId,
      image_urls: [...existingFilenames, ...uploadedFilenames], // ✅ filenames permanents uniquement
    };

    console.log("Filenames sauvegardés :", payload.image_urls);

    // Toujours créer un nouveau constat daté : un rapport de conservation ne doit
    // jamais écraser le précédent, sinon l'historique de la vie de l'œuvre est perdu.
    delete payload.id;
    const { error } = await supabase.from("conservation_reports").insert([payload]);

    if (!error) {
      alert("Rapport sauvegardé avec succès !");
    } else {
      console.error("Erreur sauvegarde :", error);
      alert("Erreur lors de la sauvegarde.");
    }

    setSaving(false);
  };

  if (loading) return <p>Chargement...</p>;

  return (
    <form
      onSubmit={handleSubmit}
      className="max-w-4xl mx-auto px-6 py-8 bg-white"
    >
      {analyzing && (
        <p className="text-sm text-gray-600 italic mb-4">
          Analyse IA en cours...
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 mt-6">
        <label className="text-sm font-medium">
          Dernier rapport complet
          <input
            type="date"
            name="last_full_report"
            value={form.last_full_report || ""}
            onChange={handleChange}
            className="form-input w-full"
          />
        </label>
        <label className="text-sm font-medium">
          Dernière inspection visuelle
          <input
            type="date"
            name="last_inspection"
            value={form.last_inspection || ""}
            onChange={handleChange}
            className="form-input w-full"
          />
        </label>
        <label className="text-sm font-medium">
          Dernier transport
          <input
            type="date"
            name="last_transport"
            value={form.last_transport || ""}
            onChange={handleChange}
            className="form-input w-full"
          />
        </label>
        <label className="text-sm font-medium">
          Niveau de risque
          <select
            name="risk_level"
            value={form.risk_level}
            onChange={handleChange}
            className="form-select w-full"
          >
            <option value="Faible">Faible</option>
            <option value="Modéré">Modéré</option>
            <option value="Élevé">Élevé</option>
          </select>
        </label>
        <label className="text-sm font-medium">
          Prochaine inspection
          <input
            type="date"
            name="next_inspection_due"
            value={form.next_inspection_due || ""}
            onChange={handleChange}
            className="form-input w-full"
          />
        </label>
        <label className="text-sm font-medium">
          Prochain rapport complet
          <input
            type="date"
            name="next_full_report_due"
            value={form.next_full_report_due || ""}
            onChange={handleChange}
            className="form-input w-full"
          />
        </label>
      </div>

      <div className="mt-6 flex items-end gap-4">
        <div className="flex-1">
          <label className="text-sm font-medium">
            Zone photographiée
            <select
              value={selectedZone}
              onChange={(e) => setSelectedZone(e.target.value)}
              className="form-select w-full mt-1"
            >
              <option value="surface">Surface</option>
              <option value="coin-haut-gauche">Coin haut gauche</option>
              <option value="coin-haut-droit">Coin haut droit</option>
              <option value="coin-bas-gauche">Coin bas gauche</option>
              <option value="coin-bas-droit">Coin bas droit</option>
              <option value="verso">Verso</option>
              <option value="encadrement">Encadrement</option>
              <option value="autre">Autre</option>
            </select>
          </label>
        </div>
        <div className="flex-1">
          <label className="text-sm font-medium">
            Ajouter des photos HD
            <input
              type="file"
              multiple
              className="form-input mt-1"
              onChange={(e) => handleImageUpload(e.target.files)}
            />
          </label>
        </div>
      </div>

      <div className="mt-4">
        <label className="text-sm font-medium">
          Recommandation IA / expert
          <textarea
            name="recommendation"
            value={form.recommendation || ""}
            onChange={handleChange}
            className="form-textarea w-full"
            rows={4}
          />
        </label>
      </div>

      {(previewUrls.length > 0 || existingImages.length > 0) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-6">
          {/* Nouveaux uploads — aperçu local */}
          {previewUrls.map((url, idx) => (
            <div key={`preview-${idx}`} className="relative group">
              <img
                src={url}
                alt={`Aperçu ${idx + 1}`}
                className="w-full h-auto rounded shadow border"
              />
              <span className="absolute bottom-1 left-1 text-xs bg-white bg-opacity-80 px-1 rounded shadow">
                {zoneMapping[idx] || ""}
              </span>
              <button
                type="button"
                onClick={() => removePreview(idx)}
                className="absolute top-1 right-1 bg-white text-red-600 rounded-full p-1 shadow hover:bg-red-100"
              >
                ✕
              </button>
            </div>
          ))}

          {/* Images existantes — URLs signées fraîches */}
          {existingImages.map((url, idx) => (
            <div key={`existing-${idx}`} className="relative group">
              <img
                src={url}
                alt={`Vue ${idx + 1}`}
                className="w-full h-auto rounded shadow border"
                onClick={() => removeExistingImage(idx)}
                title="Cliquer pour supprimer"
                style={{ cursor: "pointer" }}
              />
              <span className="absolute bottom-1 left-1 text-xs bg-white bg-opacity-80 px-1 rounded shadow">
                {existingZones[idx] || ""}
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex justify-between">
        <a
          href={`/artwork/${artworkId}`}
          className="px-4 py-2 bg-gray-200 text-gray-900 rounded shadow hover:bg-gray-300 flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Retour fiche
        </a>
        <button
          type="submit"
          className="px-4 py-2 bg-black text-white rounded shadow hover:bg-gray-800"
          disabled={saving}
        >
          {saving ? "Sauvegarde..." : report ? "Mettre à jour" : "Créer"}
        </button>
      </div>
    </form>
  );
};

export default ConservationReportForm;